import { SLUGS, FIELDS } from '@/fish/lib/taxonomy-constants'
import type { FilterDefaults } from '@/services/filter-layer-service/types.ts'
import { Vertical } from '@/types/verticals'
import { FishMode } from '@/types/modes'
/**
 * These keys tie client logic to the storage values
 * * Note: high_low, elev_min, and elev_max are all used for the high/low filter.
 * * This is because that filter is using two separate data attributes in one filter
 */
export const FILTER_KEYS = Object.freeze<Record<string, string>>({
  [SLUGS.LAUNCH]: 'filter_boat-launch',
  [SLUGS.RAMP]: 'filter_boat-ramp',
  [FIELDS.FISH_TYPES]: 'filter_fishTypes',
  [FIELDS.AMENITIES]: 'filter_amenities',
  [FIELDS.AREA]: 'filter_area',
})

export const TILE_KEYS = Object.freeze({
  TECH_RATING: 'data:technical_rating',
  LENGTH: 'data:length',
  DIFFICULTY_SYM: 'data:difficulty_symbol',
  HIKE_DIFFICULTY: 'data:hike_difficulty_display',
  BIKE_DIFFICULTY: 'data:bike_difficulty',
  TIME: 'data:time',
  HIKE_HOURS: 'data:hike_hours',
  ELEV_LOSS: 'data:elevation_loss',
  ELEV_GAIN: 'data:elevation_gain',
  ELEV_MIN: 'data:elevation_min',
  ELEV_MAX: 'data:elevation_max',
  PETS: 'data:pets',
  E_BIKES: 'data:ebike',
  ROUTE_TYPE: 'data:route_type',
  COLOR_RATING: 'data:color_rating',
  CLIMB_ALL: 'data:num_routes',
  CLIMB_BOULDER: 'data:num_boulder',
  CLIMB_SPORT: 'data:num_sport',
  CLIMB_TRAD: 'data:num_trad',
  CLIMB_TOP_ROPE: 'data:num_tr',
  CLIMB_ICE_MIXED: 'data:num_icemixed',
  FISH_TYPES: 'data:species_slugs',
  SKI_ROUTE_TYPE: 'data:ski_route_type',
}) satisfies Readonly<Record<string, string>>

// Default values for all available filters
export const FILTER_DEFAULTS = Object.freeze<FilterDefaults>({
  [Vertical.Fish]: {
    [FishMode.Freshwater]: {
      // Fish Species
      [FILTER_KEYS[FIELDS.FISH_TYPES]]: {
        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.BLUEGILL}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.BLUEGILL}`,
          slug: 'bluegill',
          label: 'Bluegill',
        },
        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.CRAPPIE}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.CRAPPIE}`,
          slug: 'crappie',
          label: 'Crappie',
        },
        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.LAKE_TROUT}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.LAKE_TROUT}`,
          slug: 'lake-trout',
          label: 'Lake trout',
        },
        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.LARGEMOUTH_BASS}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.LARGEMOUTH_BASS}`,
          slug: 'largemouth-bass',
          label: 'Largemouth bass',
        },
        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.MUSKELLUNGE}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.MUSKELLUNGE}`,
          slug: 'muskellunge',
          label: 'Muskellunge',
        },
        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.NORTHERN_PIKE}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.NORTHERN_PIKE}`,
          slug: 'northern-pike',
          label: 'Northern pike',
        },

        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.PERCH}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.PERCH}`,
          slug: 'perch',
          label: 'Perch',
        },

        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.RAINBOW_TROUT}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.RAINBOW_TROUT}`,
          slug: 'rainbow-trout',
          label: 'Rainbow trout',
        },
        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.SMALLMOUTH_BASS}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.SMALLMOUTH_BASS}`,
          slug: 'smallmouth-bass',
          label: 'Smallmouth bass',
        },

        [`${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.WALLEYE}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.FISH_TYPES]}_${SLUGS.WALLEYE}`,
          slug: 'walleye',
          label: 'Walleye',
        },
      },
      // Everything else
      [FILTER_KEYS[SLUGS.LAUNCH]]: {
        [`${FILTER_KEYS[SLUGS.LAUNCH]}_${SLUGS.CARRY_IN}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[SLUGS.LAUNCH]}_${SLUGS.CARRY_IN}`,
          slug: SLUGS.CARRY_IN,
          label: 'Carry-In',
        },
        [`${FILTER_KEYS[SLUGS.LAUNCH]}_${SLUGS.TRAILER_LAUNCH}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[SLUGS.LAUNCH]}_${SLUGS.TRAILER_LAUNCH}`,
          slug: SLUGS.TRAILER_LAUNCH,
          label: 'Trailer Accessible',
        },
      },
      [FILTER_KEYS[FIELDS.AMENITIES]]: {
        [`${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.ADA_PARKING}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.ADA_PARKING}`,
          slug: SLUGS.ADA_PARKING,
          label: 'ADA Parking',
        },
        [`${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.DOCK}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.DOCK}`,
          slug: SLUGS.DOCK,
          label: 'Boat Dock',
        },
        [`${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.TOILET}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.TOILET}`,
          slug: SLUGS.TOILET,
          label: 'Restrooms',
        },
        [`${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.TRAILER_PARKING}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[FIELDS.AMENITIES]}_${SLUGS.TRAILER_PARKING}`,
          slug: SLUGS.TRAILER_PARKING,
          label: 'Trailer Parking',
        },
      },
      [FILTER_KEYS[SLUGS.RAMP]]: {
        [`${FILTER_KEYS[SLUGS.RAMP]}_${SLUGS.PAVED_RAMP}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[SLUGS.RAMP]}_${SLUGS.PAVED_RAMP}`,
          slug: SLUGS.PAVED_RAMP,
          label: 'Paved',
        },
        [`${FILTER_KEYS[SLUGS.RAMP]}_${SLUGS.GRAVEL_RAMP}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[SLUGS.RAMP]}_${SLUGS.GRAVEL_RAMP}`,
          slug: SLUGS.GRAVEL_RAMP,
          label: 'Gravel',
        },
        [`${FILTER_KEYS[SLUGS.RAMP]}_${SLUGS.NATURAL_RAMP}`]: {
          active: false,
          displayActive: false,
          type: 'checkbox',
          id: `${FILTER_KEYS[SLUGS.RAMP]}_${SLUGS.NATURAL_RAMP}`,
          slug: SLUGS.NATURAL_RAMP,
          label: 'Natural',
        },
      },
      [FILTER_KEYS[FIELDS.AREA]]: {
        type: 'range',
        plus: true,
        min: 0,
        max: 1000,
      },
    },
  },
})

export const FILTER_DISTANCE_UNITS = Object.freeze({
  KILOMETERS: 'km',
  MILES: 'miles',
  METERS: 'm',
  FEET: 'ft',
})
